const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const ENDPOINT = `${BACKEND}/api/shop/graphql`;

async function storefront(query, variables = {}) {
  const res = await fetch(ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ query, variables }),
  });
  const json = await res.json();
  if (json.errors) throw new Error(json.errors[0]?.message || "Shopify request failed");
  return json.data;
}

export function formatMoney(amount, currencyCode = "USD") {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currencyCode,
  }).format(Number(amount || 0));
}

const CART_FRAGMENT = `
  fragment CartFields on Cart {
    id
    checkoutUrl
    totalQuantity
    cost {
      subtotalAmount { amount currencyCode }
      totalAmount { amount currencyCode }
    }
    lines(first: 100) {
      edges {
        node {
          id
          quantity
          cost { totalAmount { amount currencyCode } }
          merchandise {
            ... on ProductVariant {
              id
              title
              image { url altText }
              price { amount currencyCode }
              product { title handle }
            }
          }
        }
      }
    }
  }
`;

function mapCart(cart) {
  if (!cart) return null;
  return {
    id: cart.id,
    checkoutUrl: cart.checkoutUrl,
    totalQuantity: cart.totalQuantity,
    subtotal: cart.cost?.subtotalAmount,
    total: cart.cost?.totalAmount,
    lines: (cart.lines?.edges || []).map(({ node }) => ({
      id: node.id,
      quantity: node.quantity,
      lineTotal: node.cost?.totalAmount,
      variantId: node.merchandise?.id,
      variantTitle: node.merchandise?.title,
      image: node.merchandise?.image,
      price: node.merchandise?.price,
      productTitle: node.merchandise?.product?.title,
      productHandle: node.merchandise?.product?.handle,
    })),
  };
}

export async function fetchProducts(first = 50) {
  const query = `
    query Products($first: Int!) {
      products(first: $first, sortKey: BEST_SELLING) {
        edges {
          node {
            id
            title
            handle
            description
            availableForSale
            featuredImage { url altText }
            images(first: 6) { edges { node { url altText } } }
            options { name values }
            priceRange {
              minVariantPrice { amount currencyCode }
              maxVariantPrice { amount currencyCode }
            }
            compareAtPriceRange {
              minVariantPrice { amount currencyCode }
            }
            variants(first: 100) {
              edges {
                node {
                  id
                  title
                  availableForSale
                  price { amount currencyCode }
                  compareAtPrice { amount currencyCode }
                  selectedOptions { name value }
                  image { url altText }
                }
              }
            }
          }
        }
      }
    }
  `;
  const data = await storefront(query, { first });
  return (data.products?.edges || []).map(({ node }) => mapProductNode(node));
}

export async function fetchCollectionProducts(handle, first = 50) {
  const query = `
    query CollectionProducts($handle: String!, $first: Int!) {
      collection(handle: $handle) {
        title
        products(first: $first) {
          edges {
            node {
              id
              title
              handle
              description
              availableForSale
              featuredImage { url altText }
              images(first: 6) { edges { node { url altText } } }
              options { name values }
              priceRange {
                minVariantPrice { amount currencyCode }
                maxVariantPrice { amount currencyCode }
              }
              compareAtPriceRange {
                minVariantPrice { amount currencyCode }
              }
              variants(first: 100) {
                edges {
                  node {
                    id
                    title
                    availableForSale
                    price { amount currencyCode }
                    compareAtPrice { amount currencyCode }
                    selectedOptions { name value }
                    image { url altText }
                  }
                }
              }
            }
          }
        }
      }
    }
  `;
  const data = await storefront(query, { handle, first });
  const col = data.collection;
  if (!col) return { title: null, found: false, products: [] };
  return {
    title: col.title,
    found: true,
    products: (col.products?.edges || []).map(({ node }) => mapProductNode(node)),
  };
}


function mapProductNode(node) {
  return {
    id: node.id,
    title: node.title,
    handle: node.handle,
    description: node.description,
    descriptionHtml: node.descriptionHtml,
    availableForSale: node.availableForSale,
    featuredImage: node.featuredImage,
    images: (node.images?.edges || []).map((e) => e.node),
    options: node.options || [],
    minPrice: node.priceRange?.minVariantPrice,
    maxPrice: node.priceRange?.maxVariantPrice,
    compareAtPrice: node.compareAtPriceRange?.minVariantPrice,
    variants: (node.variants?.edges || []).map((e) => e.node),
  };
}

export async function fetchProductByHandle(handle) {
  const query = `
    query ProductByHandle($handle: String!) {
      product(handle: $handle) {
        id
        title
        handle
        description
        descriptionHtml
        availableForSale
        featuredImage { url altText }
        images(first: 12) { edges { node { url altText } } }
        options { name values }
        priceRange {
          minVariantPrice { amount currencyCode }
          maxVariantPrice { amount currencyCode }
        }
        compareAtPriceRange {
          minVariantPrice { amount currencyCode }
        }
        variants(first: 100) {
          edges {
            node {
              id
              title
              availableForSale
              price { amount currencyCode }
              compareAtPrice { amount currencyCode }
              selectedOptions { name value }
              image { url altText }
            }
          }
        }
      }
    }
  `;
  const data = await storefront(query, { handle });
  return data.product ? mapProductNode(data.product) : null;
}

export async function fetchCart(cartId) {
  const query = `
    ${CART_FRAGMENT}
    query GetCart($id: ID!) {
      cart(id: $id) { ...CartFields }
    }
  `;
  const data = await storefront(query, { id: cartId });
  return mapCart(data.cart);
}

export async function createCart(merchandiseId, quantity = 1) {
  const query = `
    ${CART_FRAGMENT}
    mutation CartCreate($lines: [CartLineInput!]!) {
      cartCreate(input: { lines: $lines }) {
        cart { ...CartFields }
        userErrors { field message }
      }
    }
  `;
  const data = await storefront(query, { lines: [{ merchandiseId, quantity }] });
  const errs = data.cartCreate?.userErrors;
  if (errs && errs.length) throw new Error(errs[0].message);
  return mapCart(data.cartCreate?.cart);
}

export async function addLine(cartId, merchandiseId, quantity = 1) {
  const query = `
    ${CART_FRAGMENT}
    mutation CartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
      cartLinesAdd(cartId: $cartId, lines: $lines) {
        cart { ...CartFields }
        userErrors { field message }
      }
    }
  `;
  const data = await storefront(query, {
    cartId,
    lines: [{ merchandiseId, quantity }],
  });
  const errs = data.cartLinesAdd?.userErrors;
  if (errs && errs.length) throw new Error(errs[0].message);
  return mapCart(data.cartLinesAdd?.cart);
}

export async function updateLine(cartId, lineId, quantity) {
  const query = `
    ${CART_FRAGMENT}
    mutation CartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
      cartLinesUpdate(cartId: $cartId, lines: $lines) {
        cart { ...CartFields }
        userErrors { field message }
      }
    }
  `;
  const data = await storefront(query, {
    cartId,
    lines: [{ id: lineId, quantity }],
  });
  const errs = data.cartLinesUpdate?.userErrors;
  if (errs && errs.length) throw new Error(errs[0].message);
  return mapCart(data.cartLinesUpdate?.cart);
}

export async function removeLine(cartId, lineId) {
  const query = `
    ${CART_FRAGMENT}
    mutation CartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
      cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
        cart { ...CartFields }
        userErrors { field message }
      }
    }
  `;
  const data = await storefront(query, { cartId, lineIds: [lineId] });
  const errs = data.cartLinesRemove?.userErrors;
  if (errs && errs.length) throw new Error(errs[0].message);
  return mapCart(data.cartLinesRemove?.cart);
}
